import React, { useId, useRef } from 'react';
import { CloudArrowUpIcon, DocumentTextIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const DEFAULT_ACCEPT = 'image/*,.pdf';
const DEFAULT_MAX_MB = 10;

/**
 * Canonical payment-proof / receipt attachment UI (dashed dropzone + indigo file chip).
 * Controlled: pass `file` (File or { name/originalName, size/fileSize }) and `onChange(file|null)`.
 */
export default function ProofAttachmentDropzone({
  file = null,
  onChange,
  label = 'Receipt / Document',
  hint = '(optional)',
  required = false,
  disabled = false,
  busy = false,
  accept = DEFAULT_ACCEPT,
  maxSizeMb = DEFAULT_MAX_MB,
  className = '',
  inputId,
}) {
  const autoId = useId();
  const id = inputId || `proof-attach-${autoId}`;
  const inputRef = useRef(null);

  const displayName = file?.name || file?.originalName || file?.fileName || '';
  const displaySize = Number(file?.size ?? file?.fileSize ?? 0) || 0;
  const hasFile = Boolean(displayName);

  const clearInput = () => {
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleSelect = (selected) => {
    if (!selected) return;
    if (selected.size > maxSizeMb * 1024 * 1024) {
      toast.error(`File size must be less than ${maxSizeMb} MB`);
      clearInput();
      return;
    }
    onChange?.(selected);
  };

  const handleClear = () => {
    clearInput();
    onChange?.(null);
  };

  const hintNode = required ? (
    <span className="text-red-500">*</span>
  ) : hint ? (
    <span className="text-gray-400 font-normal">{hint}</span>
  ) : null;

  return (
    <div className={className}>
      {label ? (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label} {hintNode}
        </label>
      ) : null}

      {hasFile ? (
        <div className="flex items-center gap-2 px-3 py-2 border border-indigo-300 bg-indigo-50 rounded-lg text-sm">
          <DocumentTextIcon className="w-4 h-4 text-indigo-500 flex-shrink-0" />
          <span className="flex-1 truncate text-gray-700">{displayName}</span>
          {displaySize > 0 ? (
            <span className="text-xs text-gray-400 flex-shrink-0">
              {(displaySize / 1024).toFixed(0)} KB
            </span>
          ) : null}
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled || busy}
            className="flex-shrink-0 p-0.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
            title="Remove file"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || busy}
          className="w-full flex flex-col items-center justify-center gap-1 px-3 py-4 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:text-gray-500 disabled:hover:bg-transparent"
        >
          {busy ? (
            <>
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
              <span>Uploading...</span>
            </>
          ) : (
            <>
              <CloudArrowUpIcon className="w-6 h-6 text-gray-400" />
              <span>Click to attach receipt or proof of payment</span>
              <span className="text-xs text-gray-400">PDF, JPG, PNG up to {maxSizeMb} MB</span>
            </>
          )}
        </button>
      )}

      <input
        id={id}
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        disabled={disabled || busy}
        onChange={(e) => {
          const selected = e.target.files?.[0] || null;
          if (selected) handleSelect(selected);
        }}
      />
    </div>
  );
}
